import { useState } from "react";
import { useParams } from "react-router";
import { AnalyticsApi } from "@/lib/api/analytics-api";
import { Button } from "@/lib/components/Button/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import Toast from "@/lib/components/Toast";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  numSelected: number;
  numAnimatable: number;
};

export default function PartialAnimationDialog({
  isOpen,
  onOpenChange,
  numSelected,
  numAnimatable,
}: Props) {
  const { teamExternalId } = useParams();
  const animateImages = useEditorStore((state) => state.service.animateImages);
  const [isLoading, setIsLoading] = useState(false);

  const firstImages =
    numAnimatable === 1 ? "first image" : `first ${numAnimatable} images`;

  const handleAnimate = async () => {
    setIsLoading(true);
    AnalyticsApi.logPartialAnimationConfirmed(
      teamExternalId!,
      numSelected,
      numAnimatable,
    );
    try {
      await animateImages(numAnimatable);
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      Toast.error("Failed to animate images");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="w-full">
          <DialogTitle className="mb-2 pr-2">
            Not enough credits for all images
          </DialogTitle>
          <DialogDescription>
            You have credits for{" "}
            <span className="font-semibold">
              {numAnimatable} image{" "}
              {numAnimatable === 1 ? "animation" : "animations"}
            </span>
            , but you have selected{" "}
            <span className="font-semibold">{numSelected} images</span>. Animate
            the first {numAnimatable === 1 ? "one" : numAnimatable} now, or
            remove some images.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={handleAnimate} isLoading={isLoading}>
            Animate {firstImages}
          </Button>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isLoading}>
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
